import React, { useEffect, useState } from "react";
import Banner2 from "../../components/Banner2";
import { Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { createProductInquiry } from "../../features/productInquiry";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import SelectDropdown from "../../components/SelectDropdown";
import UpdatedMobileNumberInput from "../../components/MobileNumberInput/mobile_input";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

const Domestic = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [countryCodes, setCountryCodes] = useState([]);
  const [countryList, setCountryList] = useState([]);

  const { data } = useSelector((state) => state.country);

  /* ---------- Countries ---------- */
  useEffect(() => {
    if (data?.length) {
      setCountryList(
        data.map((item) => ({
          label: item.name,
          value: item.name,
        }))
      );
    }
  }, [data]);

  useEffect(() => {
    const codes = Array.from(
      new Map(
        getCountries().map((code) => {
          const dialCode = "+" + getCountryCallingCode(code);
          return [dialCode, { label: dialCode, value: dialCode }];
        })
      ).values()
    ).sort((a, b) => a.value.localeCompare(b.value));

    setCountryCodes(codes);
  }, []);

  const getMaxDOB = () => {
    const today = new Date();
    return today.toISOString().split("T")[0]; // yyyy-mm-dd format
  };

  const maxDOB = getMaxDOB();

  /* ---------- Initial Values ---------- */
  const initialValues = {
    name: "",
    title: "",
    date_of_birth: "",
    nationality: "",
    email: "",
    mobile: "",
    country_code: "",
  };

  /* ---------- Validation Schema ---------- */
  const validationSchema = Yup.object({
    name: Yup.string().required(t("validation.required")),
    title: Yup.string().required(t("validation.required")),
    date_of_birth: Yup.string().required(t("validation.required")),
    nationality: Yup.string().required(t("validation.required")),
    email: Yup.string()
      .email(t("validation.invalid_email"))
      .required(t("validation.required")),
    mobile: Yup.string()
      .min(6, t("validation.invalid_mobile"))
      .required(t("validation.required")),
    country_code: Yup.string().required(t("validation.required")),
  });

  /* ---------- Submit ---------- */
  const onSubmit = async (values) => {
    try {
      const response = await dispatch(
        createProductInquiry({
          form_data: values,
          product_details: JSON.parse(
            localStorage.getItem("product_details")
          ),
        })
      ).unwrap();

      if (response.success) {
        toast.success(t("success_res"));
        navigate("/");
      }
    } catch (error) {
      toast.error(t("error_res"));
      console.error(error);
    }
  };

  return (
    <Banner2 classList="bg-grey">
      <Container>
        <Row>
          <Col className="offset-md-3" md={9}>
            <div className="insurance-title">
              <h3 className="title text-capitalize">
                {t("domestic.description")}
              </h3>
            </div>
          </Col>

          <Col md={3}>
            <div className="policy-box">
              <div className="icon-image">
                <img
                  src={require("../../assets/SVG/1-25.svg").default}
                  alt="icon"
                  className="img-fluid"
                />
              </div>
              <p className="title">{t("domestic.title")}</p>
            </div>
          </Col>

          <Col md={9}>
            <div className="policy-form">
              <div className="policy-formBox why-us-form">

                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={onSubmit}
                >
                  {({ setFieldValue, values }) => (
                    <Form>
                      <Row>

                        {/* Name */}
                        <Col lg={6} md={6}>
                          <div className="form-group">
                            <Field
                              type="text"
                              name="name"
                              placeholder={t("domestic.form.name")}
                              className="form-control element-style"
                            />
                            <ErrorMessage name="name" component="div" className="text-danger small" />
                          </div>
                        </Col>

                        {/* Title */}
                        <Col lg={6} md={6}>
                          <SelectDropdown
                            name="title"
                            placeholder={t("domestic.form.title.placeholder")}
                            selectedOption={values.title}
                            options={[
                              { value: "mr", label: t("domestic.form.title.option1") },
                              { value: "miss", label: t("domestic.form.title.option2") },
                            ]}
                            handleOnChange={(name, value) =>
                              setFieldValue(name, value)
                            }
                          />
                          <ErrorMessage name="title" component="div" className="text-danger small" />
                        </Col>

                        {/* DOB */}
                        <Col md={12}>
                          <div className="form-group">
                            <label className="form-label">
                              {t("personal_accident.form.dob")}
                            </label>
                            <Field
                              type="date"
                              max={maxDOB}
                              name="date_of_birth"
                              className="form-control element-style"
                            />
                            <ErrorMessage name="date_of_birth" component="div" className="text-danger small" />
                          </div>
                        </Col>

                        {/* Nationality */}
                        <Col md={6}>
                          <SelectDropdown
                            name="nationality"
                            placeholder={t("travelPage3.fields.select_nationality")}
                            selectedOption={values.nationality}
                            options={countryList}
                            handleOnChange={(name, value) =>
                              setFieldValue(name, value)
                            }
                          />
                          <ErrorMessage name="nationality" component="div" className="text-danger small" />
                        </Col>

                        {/* Email */}
                        <Col md={6}>
                          <div className="form-group">
                            <Field
                              type="email"
                              name="email"
                              placeholder={t("email")}
                              className="form-control element-style"
                            />
                            <ErrorMessage name="email" component="div" className="text-danger small" />
                          </div>
                        </Col>

                        {/* Mobile */}
                        <Col md={12}>
                          <UpdatedMobileNumberInput
                            countryCodes={countryCodes}
                            onChange={(code, number) => {
                              setFieldValue("country_code", code);
                              setFieldValue("mobile", number);
                            }}
                          />
                          {/* <ErrorMessage name="country_code" component="div" className="text-danger small" /> */}
                          <ErrorMessage name="mobile" component="div" className="text-danger small" />
                        </Col>

                        {/* Submit */}
                        <Col md={12}>
                          <div className="form-submitbtn mt-3">
                            <button type="submit" className="btn h-btn">
                              {t("submit")}
                            </button>
                          </div>
                        </Col>

                      </Row>
                    </Form>
                  )}
                </Formik>

              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </Banner2>
  );
};

export default Domestic;